"use client";

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
}


export default function GlobalReachSection() {
    const [mounted, setMounted] = useState(false);
    const sectionRef     = useRef(null);
    const stickyRef      = useRef(null);
    const canvasRef      = useRef(null);
    const bigTextRef     = useRef(null);
    const blackLayerRef  = useRef(null);
    const listContainerRef = useRef(null);
    const listItemsRef   = useRef<(HTMLDivElement | null)[]>([]);

    const locations = [
        {
            id: "01",
            title: "Engagement",
            subtitle: "Capturing attention meaningfully.",
            desc: "Attention is only valuable when it turns into action. We create campaigns, content, and experiences that keep people interested long enough to explore, trust, and choose your brand.",
        },
        {
            id: "02",
            title: "Exclusivity",
            subtitle: "Building identity and differentiation.",
            desc: "Your brand shouldn't blend into the crowd. We help you define what makes you different and bring that to life across every customer touchpoint, so you're remembered for the right reasons.",
        },
        {
            id: "03",
            title: "Emotion",
            subtitle: "Creating emotional recall and connection.",
            desc: "People connect with brands that feel authentic. Through thoughtful storytelling, design, and messaging, we create experiences that leave a lasting impression long after the first interaction.",
        },
        {
            id: "04",
            title: "Experience",
            subtitle: "Designing seamless, memorable brand journeys.",
            desc: "Every interaction shapes how customers perceive your business. From the first click to long-term loyalty, we build experiences that are intuitive, consistent, and worth coming back to.",
        },
    ];

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (!mounted || !canvasRef.current || !sectionRef.current) return;

        const scene = new THREE.Scene();
        let w = window.innerWidth;
        let h = window.innerHeight;

        const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
        camera.position.set(0, 0, 13);

        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true,
            antialias: true,
            powerPreference: "high-performance",
        });
        renderer.setSize(w, h);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        const globeGroup = new THREE.Group();
        scene.add(globeGroup);

        const loader = new THREE.TextureLoader();
        loader.load("/textures/earth-mask-opt.webp", (texture) => {
            const img = texture.image;
            const canvas = document.createElement("canvas");
            canvas.width  = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.drawImage(img, 0, 0);
            const data = ctx.getImageData(0, 0, img.width, img.height).data;

            const vertices = [];
            const isMobile = window.innerWidth < 768;
            const DOT_COUNT = isMobile ? 4000 : 8500;
            const radius = 3.5;

            for (let i = 0; i < DOT_COUNT; i++) {
                const phi   = Math.acos(1 - (2 * (i + 0.5)) / DOT_COUNT);
                const theta = Math.PI * (1 + Math.sqrt(5)) * i;
                const x = Math.cos(theta) * Math.sin(phi);
                const y = Math.sin(theta) * Math.sin(phi);
                const z = Math.cos(phi);
                const u = Math.atan2(x, z) / (2 * Math.PI) + 0.5;
                const v = Math.asin(y) / Math.PI + 0.5;
                const px = Math.floor(u * img.width);
                const pyFlipped = Math.floor((1 - v) * img.height);
                const index = (pyFlipped * img.width + px) * 4;
                if (data[index] > 90) vertices.push(x * radius, y * radius, z * radius);
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));

            const material = new THREE.ShaderMaterial({
                uniforms: { color: { value: new THREE.Color("#ffffff") } },
                vertexShader: `
                    void main() {
                        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                        gl_Position = projectionMatrix * mvPosition;
                        gl_PointSize = 4.0 * (12.0 / -mvPosition.z);
                    }
                `,
                fragmentShader: `
                    precision lowp float;
                    uniform vec3 color;
                    void main() {
                        vec2 cxy = 2.0 * gl_PointCoord - 1.0;
                        float r = dot(cxy, cxy);
                        float delta = fwidth(r);
                        float alpha = 1.0 - smoothstep(1.0 - delta, 1.0 + delta, r);
                        if (alpha < 0.01) discard;
                        gl_FragColor = vec4(color, alpha * ${isMobile ? "1.0" : "0.8"});
                    }
                `,
                transparent: true,
                depthTest: true,
                depthWrite: false,
            });

            const points = new THREE.Points(geometry, material);
            globeGroup.add(points);
            globeGroup.userData.points   = points;
            globeGroup.userData.material = material;
            initAnimations(material, points);
        }, undefined, (err) => console.error("Texture error:", err));

        let isInView = false;
        const observer = new IntersectionObserver(([entry]) => {
            isInView = entry.isIntersecting;
        }, { threshold: 0.1 });
        observer.observe(sectionRef.current);

        renderer.setAnimationLoop(() => {
            if (isInView) renderer.render(scene, camera);
        });

        const isMobile = window.innerWidth < 768;
        globeGroup.position.set(isMobile ? -3.5 : 0, isMobile ? 0 : -4.8, 0);
        const initialScale = isMobile ? 1.2 : 1.5;
        globeGroup.scale.set(initialScale, initialScale, initialScale);

        function initAnimations(_dotMat: THREE.ShaderMaterial, pointsMesh: THREE.Points) {
            const tl = gsap.timeline({
                scrollTrigger: {
                    id: "global-reach-trigger",
                    trigger: sectionRef.current,
                    start: "top top",
                    end: "bottom bottom",
                    scrub: isMobile ? 0.5 : 2,
                },
            });

            if (isMobile) {
                tl.to(pointsMesh.rotation, { y: "+=3.5", x: "+=0.3", duration: 20, ease: "none" });
                tl.to(listContainerRef.current, { y: -900, duration: 20, ease: "none" }, 0);
            } else {
                tl.addLabel("entry");
                tl.to(blackLayerRef.current,  { y: "0%",   duration: 6, ease: "power2.inOut" }, "entry");
                tl.to(globeGroup.position,    { y: 0, x: -3.5, z: 0, duration: 8, ease: "power2.inOut" }, "entry");
                tl.to(globeGroup.scale,       { x: 0.9, y: 0.9, z: 0.9, duration: 8, ease: "power2.inOut" }, "entry");
                tl.to(bigTextRef.current,     { y: -250, opacity: 0, duration: 5 }, "entry");
                tl.fromTo(
                    listItemsRef.current,
                    { opacity: 0, y: 50 },
                    { opacity: 1, y: 0, duration: 3, stagger: 3, ease: "power2.out" },
                    "entry+=2.5"
                );
                tl.to(listContainerRef.current, { y: -680, duration: 10, ease: "none" }, "entry+=2.5");
                tl.to(pointsMesh.rotation, { y: "+=3.5", x: "+=0.3", duration: 12, ease: "none" }, "entry");
            }
        }

        const handleResize = () => {
            const newW = window.innerWidth;
            const newH = window.innerHeight;
            renderer.setSize(newW, newH);
            camera.aspect = newW / newH;
            camera.updateProjectionMatrix();
            if (newW < 768) {
                globeGroup.scale.set(1.2, 1.2, 1.2);
                if (!ScrollTrigger.getById("global-reach-trigger")?.isActive) {
                    globeGroup.position.set(-3.5, 0, 0);
                }
            } else {
                globeGroup.scale.set(1.5, 1.5, 1.5);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            observer.disconnect();
            ScrollTrigger.getAll().forEach((t) => {
                if (t.trigger === sectionRef.current) t.kill();
            });
            renderer.setAnimationLoop(null);
            renderer.dispose();
            if (globeGroup.userData.points) {
                globeGroup.userData.points.geometry.dispose();
                globeGroup.userData.points.material.dispose();
            }
        };
    }, [mounted]);

    if (!mounted) return <section className="relative w-full h-[400vh] md:h-[270vh] bg-black" />;

    return (
        <section
            ref={sectionRef}
            className="relative w-full h-[400vh] md:h-[270vh] bg-black"
            data-theme="dark"
        >
            <div ref={stickyRef} className="sticky top-0 w-full h-screen overflow-hidden">

                {/* Background */}
                <div
                    className="absolute inset-x-0 top-0 z-0 h-full pointer-events-none"
                    style={{ background: '#050505' }}
                />

                {/* ── Radial vignette always on top of bg ── */}
                <div
                    className="absolute inset-0 z-[2] pointer-events-none"
                    style={{
                        background: 'radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.88) 100%)',
                    }}
                />

                {/* Big title */}
                <div className="absolute inset-x-0 top-0 z-[3] flex items-start justify-center pt-28 md:pt-32 pointer-events-none h-full overflow-hidden">
                    {/* Mobile */}
                    <div className="md:hidden w-full text-center px-4">
                        <h2 className="text-[14vw] font-bold tracking-tighter leading-none select-none text-white"
                            style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif", wordSpacing: '0.15em' }}>
                            The <span style={{ color: '#CEFF1A' }}>4E</span> Model
                        </h2>
                    </div>
                    {/* Desktop */}
                    <div ref={bigTextRef} className="hidden md:block w-full text-center px-4">
                        <h2 className="text-[11vw] font-bold tracking-tighter leading-none select-none whitespace-nowrap text-white"
                            style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif", wordSpacing: '0.15em' }}>
                            The <span style={{ color: '#CEFF1A' }}>4E</span> Model
                        </h2>
                    </div>
                </div>

                {/* Globe canvas */}
                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-10 md:z-40 pointer-events-none" />

                {/* Edge vignette on top of canvas */}
                <div
                    className="absolute inset-0 z-50 pointer-events-none"
                    style={{
                        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(5,5,5,0.92) 100%)',
                    }}
                />

                {/* Content overlay (slides up) */}
                <div
                    ref={blackLayerRef}
                    className="absolute inset-0 z-20 md:z-30 flex items-start justify-center md:justify-end px-5 md:px-24 pt-28 md:pt-40 translate-y-0 md:translate-y-full will-change-transform"
                    style={{ background: 'transparent' }}
                >
                    <div
                        ref={listContainerRef}
                        className="w-full md:w-1/2 flex flex-col gap-6 md:gap-10 text-white md:pl-12 md:mt-[48vh] pb-[10vh] md:pb-0"
                    >
                        {locations.map((loc, i) => (
                            <div
                                key={loc.id}
                                ref={(el) => { listItemsRef.current[i] = el; }}
                                className="flex flex-col gap-2 opacity-100 md:opacity-0 border-b border-white/5 md:border-b-0 pb-6 md:pb-0 last:border-0 mb-6 md:mb-0"
                            >
                                <div className="p-6 rounded-2xl bg-[#111] border border-white/5 md:bg-transparent md:border-0 md:p-0">
                                    <div className="flex items-center gap-4 text-gray-500 font-mono text-sm">
                                        <span>{loc.id}</span>
                                        <span className="h-[1px] w-8 bg-gray-700" />
                                        {loc.subtitle && (
                                            <span className="text-gray-500 italic text-sm md:text-base leading-relaxed">{loc.subtitle}</span>
                                        )}
                                    </div>
                                    <h3 className="text-3xl md:text-5xl font-bold mt-2 mb-2"
                                        style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif" }}>
                                        {loc.title}
                                    </h3>
                                    <p className="text-gray-400 text-base md:text-lg max-w-2xl leading-relaxed"
                                        style={{ fontFamily: "var(--font-manrope, Manrope, sans-serif)" }}>
                                        {loc.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
